import { resolveAlarmSafeProductName } from '../../../../common/helpers/alarmSafeNotes'
import { efficiencyKeys } from '../../../../common/helpers/helpers'
import {
  S4_PRODUCT_NAME,
  isS4Header,
  toProductNotes,
} from '../../../../common/helpers/productNotes'
import type {
  CustomerField,
  FieldRowable,
} from '../../../../common/schema/schema'

export const S4_CAMERA_DISPLAY_NAME = S4_PRODUCT_NAME

export interface S4CameraSummaryLine {
  readonly displayName: typeof S4_CAMERA_DISPLAY_NAME
  readonly quantity: number
}

export const isS4CameraType = (type: string): boolean =>
  resolveAlarmSafeProductName(type) === S4_CAMERA_DISPLAY_NAME

/**
 * The Job Summary stores the authoritative price on the category row. This
 * helper only gives the S4 a named, unpriced sub-row; it never allocates cost.
 */
export const getS4CameraSummaryLine = (
  rows: readonly Readonly<{ type: string; qty: unknown }>[],
): S4CameraSummaryLine | null => {
  const quantity = rows.reduce((total, row) => {
    const rowQuantity =
      typeof row.qty === 'number'
        ? row.qty
        : typeof row.qty === 'string' && row.qty.trim() !== ''
        ? Number(row.qty)
        : Number.NaN

    return isS4CameraType(row.type) &&
      Number.isFinite(rowQuantity) &&
      rowQuantity > 0
      ? total + rowQuantity
      : total
  }, 0)

  return quantity > 0 ? { displayName: S4_CAMERA_DISPLAY_NAME, quantity } : null
}

/**
 * The condensed description of the S4's own block in a category's auto notes
 * (the same `qty x Product` + short paragraph the NOTES page prints), so the
 * Job Summary sub-row reads exactly like every other product block.
 */
export const getS4CameraNoteBody = (autoNotes: string): string =>
  toProductNotes(autoNotes, true).find(({ header }) => isS4Header(header))
    ?.body ?? ''

/** The categories whose detail rows carry product photos in the Job Summary. */
export const SUMMARY_DETAIL_FIELDS: Readonly<
  Partial<Record<CustomerField, FieldRowable>>
> = {
  security_cams: 'security_cams_detail',
  security_home_automation: 'security_home_automation_detail',
  security_system: 'security_system_detail',
  security_tv: 'security_tv_detail',
  security_wifi_cameras: 'security_wifi_cameras_detail',
}

/**
 * Puts category fields in the Job Summary order (`efficiencyKeys`: cameras,
 * WiFi, system, home automation, TV), so every page that lists the security
 * categories agrees with it. A field outside that list goes last. The input
 * is not mutated.
 */
export const orderSummaryFields = <T extends string>(
  fields: readonly T[],
): T[] => {
  const rank = (field: T): number => {
    const index = (efficiencyKeys as readonly string[]).indexOf(field)
    return index < 0 ? efficiencyKeys.length : index
  }
  return [...fields].sort((a, b) => rank(a) - rank(b))
}

export interface ProductPhoto {
  readonly type: string
  readonly url: string
}

export interface RowProductPhotos {
  /** The thumbnails of the category row, in detail-row order, no duplicates. */
  readonly categoryPhotos: readonly ProductPhoto[]
  /** The S4 thumbnail, shown on its own sub-row when the row has an S4. */
  readonly s4Photo: ProductPhoto | undefined
}

/**
 * The product photos one Job Summary category shows: one per product type
 * with a photo, first occurrence wins. When the category has an S4 sub-row,
 * the S4 photo moves to it (Monday 13246307524).
 */
export const getRowProductPhotos = (
  rows: readonly object[],
  photoFor: (type: string) => string | undefined,
): RowProductPhotos => {
  const seen = new Set<string>()
  const photos: ProductPhoto[] = []
  // Detail rows are a union of row shapes; only `type` and `qty` matter here.
  const detailRows = rows as readonly Readonly<{
    qty?: unknown
    type?: unknown
  }>[]
  for (const row of detailRows) {
    const type = typeof row.type === 'string' ? row.type : ''
    const url = type ? photoFor(type) : undefined
    if (type && url && !seen.has(type)) {
      seen.add(type)
      photos.push({ type, url })
    }
  }
  const hasS4Row = !!getS4CameraSummaryLine(
    detailRows.map((row) => ({
      qty: row.qty,
      type: typeof row.type === 'string' ? row.type : '',
    })),
  )
  return hasS4Row
    ? {
        categoryPhotos: photos.filter(({ type }) => !isS4CameraType(type)),
        s4Photo: photos.find(({ type }) => isS4CameraType(type)),
      }
    : { categoryPhotos: photos, s4Photo: undefined }
}

export interface ViewerProduct {
  /** `field:type`: the same type can sit in two categories. */
  readonly key: string
  readonly photo: string
  readonly type: string
}

export const viewerProductKey = (field: string, type: string): string =>
  `${field}:${type}`

/**
 * Every product the Job Summary viewer can step through (Monday 13246279222),
 * in the order the customer sees the thumbnails: category by category, the
 * row's photos first, then its S4 sub-row.
 */
export const getViewerProducts = (
  categories: readonly Readonly<{
    field: string
    rows: readonly object[]
  }>[],
  photoFor: (type: string) => string | undefined,
): readonly ViewerProduct[] =>
  categories.flatMap(({ field, rows }) => {
    const { categoryPhotos, s4Photo } = getRowProductPhotos(rows, photoFor)
    return [...categoryPhotos, ...(s4Photo ? [s4Photo] : [])].map(
      ({ type, url }) => ({
        key: viewerProductKey(field, type),
        photo: url,
        type,
      }),
    )
  })
